import { Color3, CreateBox, Matrix, PointLight, Quaternion, TransformNode, Vector3, type Mesh, type Scene } from "@babylonjs/core";
import {
  ANCHOR, LIVERY, ROPES, SAILS, SHIP, SHIP_POINTS, UPGRADES, breachSeverity, cannonAim, cannonFallenRotation, capstanTurn, defaultLivery,
  islandCannonAim, ropeCut, sailDamaged, sailHalfAt, sailHoles, sailShredded, sailSpan, tier, wheelBroken,
  type CannonState, type IslandCannonMount, type Livery, type PlayerState, type Upgrades, type Vec3,
} from "@tiny-seas/shared";
import type { Environment } from "./environment.ts";
import { PALETTE } from "./materials.ts";
import { HullDynamics, hullImmersion, type HullParams, type ShipMotion } from "./waves.ts";
import { spawnModel, type ModelInstance } from "./models.ts";
import { SailCloth } from "./sail-damage.ts";

/** Rope attachment on the anchor ring, above the anchor pivot (tools/models/ship.py). */
const ANCHOR_RING_TOP = 0.82;
/** Pawl handle swing when kicked off the ratchet, and its lift riding over a tooth. */
const PAWL_KICK = -0.85;
const PAWL_TOOTH_LIFT = 0.12;
/** Bell swing after a ring: seconds, peak angle, decay rate and swing period. */
const BELL_SWING = { seconds: 3, angle: 0.5, decay: 1.6, period: 1.1 } as const;
/** Balls shown in the six shot racks (three sockets each) when the hold is full. */
const RACK_SOCKETS = 3;
const GUN_FINISH = ["Iron", "Banded", "Bronze"] as const;
/** The sail cell the emblem is painted across (row 1, column 1). */
const EMBLEM_CELL = 4;
/** Sail swing (radians) flogging on one or both cut sheets, and a torn-away sail's strips stirring. */
const FLOG = { one: 0.1, both: 0.2, belly: 0.4, lift: 0.07, torn: 0.06 } as const;

/** Replicated hull, rigging, cloth and helm damage (`ShipState` fields; guns come with `updateCannons`). */
export interface ShipDamage {
  leaks: number;
  breaches: number;
  ropes: number;
  sailHoles: number;
  wheelHp: number;
}

/**
 * Turns `out` so a pivot's local +Y points from a to b and returns the distance (the pivot's
 * y scale for a one-metre authored rope). Allocation-free.
 */
export function aimSegment(a: Readonly<Vec3>, b: Readonly<Vec3>, out: Quaternion): number {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const length = Math.hypot(dx, dy, dz);
  if (length < 1e-6) { out.set(0, 0, 0, 1); return 0; }
  AIM_DIR.set(dx / length, dy / length, dz / length);
  Quaternion.FromUnitVectorsToRef(Vector3.UpReadOnly, AIM_DIR, out);
  return length;
}
const AIM_DIR = new Vector3();

/** Ship-local offset of a pivot's parent (model pivots carry translation only). */
function parentOffset(node: TransformNode, root: TransformNode): Vector3 {
  const out = Vector3.Zero();
  for (let n = node.parent as TransformNode | null; n && n !== root; n = n.parent as TransformNode | null) out.addInPlace(n.position);
  return out;
}

interface Sheet {
  rope: number;
  sail: number;
  side: -1 | 1;
  intact: TransformNode;
  tail: TransformNode;
  /** Deck anchor of the intact pivot, ship-local. */
  anchor: Vec3;
  intactParent: Vector3;
  tailParent: Vector3;
  /** Rendered clew, ship-local, written each frame. */
  clew: Vec3;
}


const cap = (name: string): string => name[0]!.toUpperCase() + name.slice(1);
/** Variant pivot names in tools/models/ship.py, derived from the shared livery and tier lists. */
export const SHIP_LOOK_PARTS = {
  figurehead: (i: number) => `figurehead${cap(LIVERY.figureheads[i]!)}`,
  emblem: (i: number) => `emblem${cap(LIVERY.emblems[i]!)}`,
  flag: (i: number) => `flag${cap(LIVERY.flags[i]!)}`,
  cannon: (gun: number, guns: number) => `cannon${gun}${GUN_FINISH[guns]}`,
  hull: (level: number) => `hullTier${level}`,
  hold: (level: number) => `holdTier${level}`,
} as const;

/** Enables exactly one variant per look slot: figurehead, sail emblem, flag, gun finish, hull and hold tier props. */
export function applyShipLook(model: ModelInstance, livery: Livery, upgrades: Upgrades): void {
  const enable = (name: string, on: boolean): void => model.part(name).setEnabled(on);
  for (let i = 0; i < LIVERY.figureheads.length; i++) enable(SHIP_LOOK_PARTS.figurehead(i), i === livery.figurehead);
  for (let i = 0; i < LIVERY.emblems.length; i++) enable(SHIP_LOOK_PARTS.emblem(i), i === livery.emblem);
  for (let i = 0; i < LIVERY.flags.length; i++) enable(SHIP_LOOK_PARTS.flag(i), i === livery.flag);
  const guns = tier(upgrades.guns), hull = tier(upgrades.hull), hold = tier(upgrades.hold);
  for (let gun = 0; gun < SHIP_POINTS.cannons.length; gun++) {
    for (let t = 0; t < GUN_FINISH.length; t++) enable(SHIP_LOOK_PARTS.cannon(gun, t), t === guns);
  }
  for (let level = 1; level <= UPGRADES.maxTier; level++) enable(SHIP_LOOK_PARTS.hull(level), hull >= level);
  for (let level = 0; level <= UPGRADES.maxTier; level++) enable(SHIP_LOOK_PARTS.hold(level), level === hold);
}

/** Dark emblem paint on light sails, cream on dark ones. */
export function emblemColor(sail: Color3): string {
  const linear = sail.toLinearSpace();
  return 0.2126 * linear.r + 0.7152 * linear.g + 0.0722 * linear.b > 0.45 ? "#2a211b" : "#efe6d2";
}

/**
 * The player ship (tools/models/ship.py), authored against the same
 * ship-local collision boxes the simulation uses so what you see is what you
 * stand on. `root` is placed at the interpolated ship pose; passengers and
 * deck chests are parented to it.
 */
export class ShipModel {
  readonly root: TransformNode;
  private readonly model: ModelInstance;
  private readonly sails: TransformNode[];
  private readonly wheel: TransformNode;
  private readonly flag: TransformNode;
  private readonly ensign: TransformNode;
  private readonly bell: TransformNode;
  private readonly nestScope: TransformNode;
  private readonly anchor: TransformNode;
  private readonly anchorRope: TransformNode;
  private readonly capstan: TransformNode;
  private readonly capstanCoil: TransformNode;
  private readonly capstanPawl: TransformNode;
  private readonly leaks: TransformNode[] = [];
  private readonly patches: TransformNode[] = [];
  private readonly flames: TransformNode[];
  /** rack[gun][n - 1] holds n balls. */
  private readonly racks: TransformNode[][];
  private readonly lantern: PointLight;
  private readonly floodWater: Mesh;
  private readonly cannonBarrels: TransformNode[] = [];
  private readonly aim = { yaw: 0, elevation: 0 };
  private readonly env: Environment;
  private sailLevel = 0;
  /** Rendered anchor depth, easing toward the replicated one. */
  private anchorShown = 1;
  private time = Math.random() * 10;
  private lookKey = "";
  private lanternBits = -1;
  private litLanterns = 0;
  private rackKey = "";
  /** Seconds since the last bell ring (≥ BELL_SWING.seconds once at rest). */
  private bellTime: number = BELL_SWING.seconds;
  /** The waterplane under the hull (`Environment.shipMotion` fills it); `ride` springs the hull towards it. */
  readonly waterPlane: ShipMotion = { heave: 0, pitch: 0, roll: 0 };
  /** The hull's own pose on the waves; `update` applies it under the sinking pose. */
  readonly waveMotion: ShipMotion;
  /** Bow and stern depth below the surrounding water (m), from the last `ride`. */
  readonly immersion = { bow: SHIP.draft, stern: SHIP.draft };
  private readonly dynamics = new HullDynamics();
  private lastX = Number.NaN;
  private lastZ = Number.NaN;
  private readonly scene: Scene;
  /** rope{i}Intact / rope{i}Cut for every ROPES entry. */
  private readonly ropeIntact: TransformNode[] = [];
  private readonly ropeCutNodes: TransformNode[] = [];
  private readonly sheets: Sheet[] = [];
  /** Per sail: cut sheet count and the side (-1/1) whose sheet alone is cut, else 0. */
  private readonly sheetsCut = [0, 0, 0];
  private readonly looseSide = [0, 0, 0];
  private readonly sailTorn = [false, false, false];
  /** Per sail: its sailcloth meshes, and the damaged clone while it is holed or torn. */
  private readonly sailCloth: Mesh[][];
  private readonly sailDamage: (SailCloth | null)[] = [null, null, null];
  private readonly emblems: TransformNode[];
  private emblemHidden = false;
  private readonly wheelIntact: TransformNode;
  private readonly wheelBrokenNode: TransformNode;
  private readonly wheelDebris: TransformNode;
  private helmSmashed = false;
  private readonly breaches: TransformNode[] = [];
  /** Rendered hole severity per leak slot (0 sound). */
  private readonly severity = [0, 0, 0, 0];
  private readonly carriages: TransformNode[] = [];
  private readonly wrecks: TransformNode[] = [];
  private readonly fallen: TransformNode[] = [];
  /** Each barrel pivot's authored (mounted) position. */
  private readonly barrelRest: Vector3[] = [];
  private wreckedBits = 0;
  private readonly fallenRotation: Vec3 = { x: 0, y: 0, z: 0 };
  /** Last applied damage (`setDamage`); leaks starts invalid so the first call applies. */
  private leaksShown = -1;
  private breachesShown = 0;
  private ropesShown = 0;
  private sailsShown = 0;
  private wheelHpShown = 0;
  private readonly clewMatrix = new Matrix();
  private readonly clewRotation = new Quaternion();
  private readonly clewPoint = new Vector3();
  private liveryEmblem = 0;

  constructor(scene: Scene, env: Environment, color: string) {
    this.env = env;
    this.scene = scene;
    this.waveMotion = this.dynamics.pose;
    const m = env.mats;
    const sailColor = Color3.Lerp(Color3.FromHexString(PALETTE.sail), Color3.FromHexString(color), 0.6);
    const spray = m.get("water", { alpha: 0.75, emissive: 0.25 });
    const model = spawnModel(scene, m, "ship", {
      colors: { crew: color, crewCloth: color, crewSail: sailColor.toHexString(), emblem: emblemColor(sailColor) },
      materials: { spray },
      surfaceSet: "ship",
    });
    this.model = model;
    this.root = model.root;
    for (const mesh of model.meshes) {
      mesh.receiveShadows = true;
      if (mesh.material !== spray) env.addCaster(mesh);
    }
    this.sails = ["sail0", "sail1", "sail2"].map((name) => model.part(name));
    this.wheel = model.part("wheel");
    this.flag = model.part("flag");
    this.ensign = model.part("ensign");
    this.bell = model.part("bell");
    this.nestScope = model.part("nestScope");
    this.anchor = model.part("anchor");
    this.anchorRope = model.part("anchorRope");
    this.capstan = model.part("capstanRotor");
    this.capstanCoil = model.part("capstanCoil");
    this.capstanPawl = model.part("capstanPawl");
    this.applyAnchor(false);
    for (let i = 0; i < SHIP_POINTS.leaks.length; i++) {
      const leak = model.part(`leak${i}`);
      leak.setEnabled(false);
      this.leaks.push(leak);
      const patch = model.part(`patch${i}`);
      patch.setEnabled(false);
      this.patches.push(patch);
    }
    SHIP_POINTS.cannons.forEach((mount, i) => {
      const pivot = model.part(`cannon${i}`);
      pivot.rotation.y = (mount.side * Math.PI) / 2;
      this.cannonBarrels.push(pivot);
      this.barrelRest.push(pivot.position.clone());
      this.carriages.push(model.part(`cannonCarriage${i}`));
      this.wrecks.push(model.part(`cannonWreck${i}`));
      this.fallen.push(model.part(`cannonFallen${i}`));
      this.wrecks[i]!.setEnabled(false);
    });
    this.flames = SHIP_POINTS.lanterns.map((_, i) => model.part(`lanternFlame${i}`));
    this.racks = SHIP_POINTS.cannons.map((_, i) => Array.from({ length: RACK_SOCKETS }, (_, n) => model.part(`rack${i}_${n + 1}`)));
    for (let i = 0; i < SHIP_POINTS.leaks.length; i++) {
      const breach = model.part(`breach${i}`);
      breach.setEnabled(false);
      this.breaches.push(breach);
    }
    ROPES.forEach((rope, i) => {
      const intact = model.part(`rope${i}Intact`), cut = model.part(`rope${i}Cut`);
      cut.setEnabled(false);
      this.ropeIntact.push(intact);
      this.ropeCutNodes.push(cut);
      if (!rope.sheet) return;
      const tail = model.part(`rope${i}Tail`);
      tail.setEnabled(false);
      // The client aims the sheet pivot with a quaternion and stretches it along +Y.
      intact.rotationQuaternion = new Quaternion();
      const intactParent = parentOffset(intact, this.root);
      this.sheets.push({
        rope: i, sail: rope.sheet.sail, side: rope.sheet.side, intact, tail,
        anchor: intactParent.add(intact.position), intactParent, tailParent: parentOffset(tail, this.root), clew: { x: 0, y: 0, z: 0 },
      });
    });
    this.wheelIntact = model.part("wheelIntact");
    this.wheelBrokenNode = model.part("wheelBroken");
    this.wheelDebris = model.part("wheelDebris");
    this.wheelBrokenNode.setEnabled(false);
    this.wheelDebris.setEnabled(false);
    const fabric = m.ship("fabric");
    this.sailCloth = this.sails.map((sail) => model.meshes.filter((mesh) => mesh.parent === sail && mesh.material === fabric));
    this.emblems = LIVERY.emblems.map((_, i) => model.part(SHIP_LOOK_PARTS.emblem(i)));

    // One warm light per ship for its lit lanterns; it touches only this hull.
    this.lantern = new PointLight("ship-lantern", Vector3.Zero(), scene);
    this.lantern.parent = this.root;
    this.lantern.diffuse = Color3.FromHexString("#ffd28a").toLinearSpace();
    this.lantern.range = 10;
    this.lantern.radius = 0.12;
    this.lantern.includedOnlyMeshes = model.meshes;

    // Floodwater rises across the waist as flooding grows.
    this.floodWater = CreateBox("flood", { width: SHIP.beam - 0.3, height: 0.02, depth: 11.8 }, scene);
    this.floodWater.material = m.get("water", { alpha: 0.55 });
    this.floodWater.parent = this.root;
    this.floodWater.position.set(0, SHIP.deckHeight, 0);
    this.floodWater.isPickable = false;
    this.floodWater.setEnabled(false);

    this.applyLook(defaultLivery(0, { figurehead: 0, emblem: 0, flag: 0 }), { hull: 0, guns: 0, hold: 0 });
    this.setLanterns(15);
    this.updateRacks(1, 1);
  }

  /** Livery and upgrade tiers; only re-applies when they change. */
  applyLook(livery: Livery, upgrades: Upgrades): void {
    const key = `${livery.figurehead}:${livery.emblem}:${livery.flag}:${upgrades.hull}:${upgrades.guns}:${upgrades.hold}`;
    if (key === this.lookKey) return;
    this.lookKey = key;
    applyShipLook(this.model, livery, upgrades);
    this.liveryEmblem = livery.emblem;
    if (this.emblemHidden) for (const emblem of this.emblems) emblem.setEnabled(false);
  }

  /** Bit i lights `SHIP_POINTS.lanterns[i]` (and, for the stern lantern, the gallery windows). */
  setLanterns(bits: number): void {
    if (bits === this.lanternBits) return;
    this.lanternBits = bits;
    let lit = 0, x = 0, y = 0, z = 0;
    SHIP_POINTS.lanterns.forEach((p, i) => {
      const on = (bits & (1 << i)) !== 0;
      this.flames[i]!.setEnabled(on);
      if (!on) return;
      lit++; x += p.x; y += p.y; z += p.z;
    });
    this.litLanterns = lit;
    this.lantern.setEnabled(lit > 0);
    if (lit) this.lantern.position.set(x / lit, y / lit, z / lit);
  }

  /** Swings the bell from rest. */
  ringBell(): void {
    this.bellTime = 0;
  }

  /** Shot racks show the hold's cannonballs spread across the six guns (at least one ball while any remain). */
  updateRacks(balls: number, max: number): void {
    const sockets = SHIP_POINTS.cannons.length * RACK_SOCKETS;
    const shown = balls <= 0 ? 0 : Math.max(1, Math.round((sockets * Math.min(balls, max)) / max));
    const key = String(shown);
    if (key === this.rackKey) return;
    this.rackKey = key;
    const guns = SHIP_POINTS.cannons.length;
    this.racks.forEach((levels, i) => {
      const fill = Math.floor(shown / guns) + (i < shown % guns ? 1 : 0);
      levels.forEach((node, n) => node.setEnabled(n + 1 === fill));
    });
  }

  /** Crow's nest telescope follows its user's ship-local look. */
  updateNestScope(yawLocal: number, pitch: number): void {
    this.nestScope.rotation.set(-pitch, yawLocal, 0);
  }

  /** Animated, authored wheel pivot for first-person and remote world-space grips. */
  get wheelNode(): TransformNode {
    return this.wheel;
  }

  /** Animated, authored barrel pivot for cannon `index`, matching SHIP_POINTS.cannons; null while the gun lies dismounted. */
  cannonNode(index: number): TransformNode | null {
    return this.wreckedBits & (1 << index) ? null : this.cannonBarrels[index] ?? null;
  }

  /** Turning capstan head; its bars carry the pushers' hands (and the lab's authored grips). */
  get capstanNode(): TransformNode {
    return this.capstan;
  }

  /** Rendered head rotation; pushers' feet follow `capstanStation` at this same turn. */
  get capstanTurn(): number {
    return this.capstan.rotation.y;
  }

  /** Real hull repair surface, including its authored inward face offset. */
  patchNode(slot: number): TransformNode | null {
    return this.patches[slot] ?? null;
  }

  /**
   * Springs the hull towards `waterPlane` by `dt`. A hull that jumped (respawn, teleport) and
   * reduced motion snap straight onto the water.
   */
  ride(dt: number, params: Readonly<HullParams>, reducedMotion: boolean): ShipMotion {
    const x = this.root.position.x, z = this.root.position.z;
    const jumped = !(Math.hypot(x - this.lastX, z - this.lastZ) < 30);
    this.lastX = x;
    this.lastZ = z;
    this.dynamics.step(dt, this.waterPlane, params, jumped || reducedMotion);
    hullImmersion(this.waterPlane, this.waveMotion, this.immersion);
    return this.waveMotion;
  }

  /**
   * Visual state. `sinkProgress` 0…1 lowers and tilts the ship during sinking, on top of
   * `waveMotion`; the root carries passengers, so they go down with it.
   */
  update(dt: number, sail: number, rudder: number, leaks: number, flooding: number, sinkProgress: number, speed: number, reducedMotion: boolean): void {
    this.time += dt;
    const target = sail / 3;
    this.sailLevel += (target - this.sailLevel) * Math.min(1, dt * 3);
    const level = this.sailLevel, span = sailSpan(level);
    const wind = Math.min(1, Math.abs(speed) / SHIP.sailSpeeds[3]!) * level;
    for (let i = 0; i < this.sails.length; i++) {
      const s = this.sails[i]!, cut = this.sheetsCut[i]!, phase = this.time + i * 1.7;
      // A sail loose on a cut sheet flogs fore and aft, its belly snapping; one free clew lifts.
      const flog = (cut === 0 ? 0 : cut === 1 ? FLOG.one : FLOG.both) * level;
      const lift = this.looseSide[i]! * FLOG.lift * level;
      s.scaling.y = span;
      if (reducedMotion) {
        s.scaling.z = 1;
        s.rotation.set(0, 0, lift);
        continue;
      }
      const belly = cut > 0 ? FLOG.belly * (flog / FLOG.both) * Math.sin(phase * 9.3) : wind * (0.025 + Math.sin(this.time * 1.9) * 0.015);
      s.scaling.z = 1 + belly;
      s.rotation.x = flog * (0.7 * Math.sin(phase * 6.1) + 0.3 * Math.sin(phase * 13.7)) + (this.sailTorn[i] ? FLOG.torn * Math.sin(phase * 3.1) : 0);
      s.rotation.y = Math.sin(this.time * 4.3) * wind * 0.02;
      s.rotation.z = lift * (1 + 0.6 * Math.sin(phase * 8.3));
    }
    this.updateSheets(reducedMotion);
    const wave = (t: number): number => reducedMotion ? 0 : Math.sin(t * 2.1) * 0.18 + Math.sin(t * 5.3) * 0.05;
    this.flag.rotation.y = wave(this.time);
    this.ensign.rotation.y = wave(this.time + 1.3);
    this.bellTime += dt;
    const t = this.bellTime;
    this.bell.rotation.x = reducedMotion || t >= BELL_SWING.seconds ? 0
      : BELL_SWING.angle * Math.exp(-BELL_SWING.decay * t) * Math.sin((2 * Math.PI * t) / BELL_SWING.period);
    this.lantern.intensity = ((4 + 12 * this.env.weather.storm) * this.litLanterns) / SHIP_POINTS.lanterns.length;
    if (this.helmSmashed) this.wheel.rotation.z = 0;
    else this.wheel.rotation.z += (-rudder * 1.6 - this.wheel.rotation.z) * (1 - Math.exp(-10 * dt));
    this.anchor.rotation.y = reducedMotion ? 0 : Math.sin(this.time * 0.7) * 0.5 * this.anchorShown;
    for (let i = 0; i < this.leaks.length; i++) {
      const open = (leaks & (1 << i)) !== 0;
      const spray = this.leaks[i]!;
      spray.setEnabled(open);
      if (!open) continue;
      // A wider hole throws a fuller jet.
      const size = 0.7 + 0.3 * Math.max(1, this.severity[i]!);
      spray.scaling.set(size, size * (0.85 + Math.sin(this.time * 18 + i) * 0.2), size);
    }
    this.floodWater.setEnabled(flooding > 1);
    this.floodWater.position.y = SHIP.deckHeight + Math.min(1, flooding / 100) * 0.6;
    this.root.position.y = this.waveMotion.heave - sinkProgress * 6;
    this.root.rotation.z = this.waveMotion.roll + sinkProgress * 0.25;
    this.root.rotation.x = this.waveMotion.pitch - sinkProgress * 0.12;
  }

  /**
   * Rendered clew of sail `sail` on `side`: the sail's furled clew carried through its live flog
   * and sway, ship-local. Equals `sailClew(sail, side, level)` when the sail hangs still.
   */
  private clewOf(sail: number, side: -1 | 1, out: Vec3): void {
    const node = this.sails[sail]!, spec = SAILS[sail]!, r = node.rotation;
    Quaternion.RotationYawPitchRollToRef(r.y, r.x, r.z, this.clewRotation);
    Matrix.ComposeToRef(node.scaling, this.clewRotation, node.position, this.clewMatrix);
    Vector3.TransformCoordinatesFromFloatsToRef(side * sailHalfAt(spec, 1), -spec.drop, 0, this.clewMatrix, this.clewPoint);
    out.x = this.clewPoint.x; out.y = this.clewPoint.y; out.z = this.clewPoint.z;
  }

  /** Intact sheets run from their deck anchor to the clew; a cut sheet's end hangs from the clew. */
  private updateSheets(reducedMotion: boolean): void {
    for (const sheet of this.sheets) {
      this.clewOf(sheet.sail, sheet.side, sheet.clew);
      if (!ropeCut(this.ropesShown, sheet.rope)) {
        sheet.intact.scaling.y = aimSegment(sheet.anchor, sheet.clew, sheet.intact.rotationQuaternion!);
        continue;
      }
      const tail = sheet.tail, c = sheet.clew, p = sheet.tailParent, phase = this.time * 3.7 + sheet.rope;
      tail.position.set(c.x - p.x, c.y - p.y, c.z - p.z);
      tail.rotation.x = reducedMotion ? 0 : 0.25 * Math.sin(phase) + 0.1 * Math.sin(phase * 2.3);
      tail.rotation.z = reducedMotion ? 0 : 0.18 * Math.sin(phase * 0.8 + 1);
    }
  }

  /**
   * Replicated damage: cut ropes, holed and torn sails, the smashed helm and hull holes.
   * Cheap when nothing changed, so it may run every frame.
   */
  setDamage(d: Readonly<ShipDamage>): void {
    if (d.leaks === this.leaksShown && d.breaches === this.breachesShown && d.ropes === this.ropesShown &&
      d.sailHoles === this.sailsShown && d.wheelHp === this.wheelHpShown) return;
    this.leaksShown = d.leaks; this.breachesShown = d.breaches; this.ropesShown = d.ropes;
    this.sailsShown = d.sailHoles; this.wheelHpShown = d.wheelHp;
    for (let i = 0; i < ROPES.length; i++) {
      const cut = ropeCut(d.ropes, i);
      this.ropeIntact[i]!.setEnabled(!cut);
      this.ropeCutNodes[i]!.setEnabled(cut);
    }
    this.sheetsCut.fill(0);
    this.looseSide.fill(0);
    for (const sheet of this.sheets) {
      const cut = ropeCut(d.ropes, sheet.rope);
      sheet.tail.setEnabled(cut);
      if (!cut) continue;
      const count = ++this.sheetsCut[sheet.sail]!;
      this.looseSide[sheet.sail] = count === 1 ? sheet.side : 0;
    }
    for (let i = 0; i < SAILS.length; i++) {
      this.sailTorn[i] = sailShredded(d.sailHoles, i);
      const damaged = sailDamaged(d.sailHoles, i);
      let cloth = this.sailDamage[i] ?? null;
      if (damaged && !cloth) cloth = this.sailDamage[i] = new SailCloth(this.scene, i, this.sailCloth[i]!, this.env.mats.ship("fabric"));
      else if (!damaged && cloth) { cloth.dispose(); cloth = this.sailDamage[i] = null; }
      cloth?.apply(d.sailHoles);
    }
    // The emblem is painted across the centre cell.
    const hideEmblem = this.sailTorn[0]! || (sailHoles(d.sailHoles, 0) & (1 << EMBLEM_CELL)) !== 0;
    if (hideEmblem !== this.emblemHidden) {
      this.emblemHidden = hideEmblem;
      this.emblems.forEach((emblem, i) => emblem.setEnabled(!hideEmblem && i === this.liveryEmblem));
    }
    const smashed = wheelBroken(d);
    this.helmSmashed = smashed;
    this.wheelIntact.setEnabled(!smashed);
    this.wheelBrokenNode.setEnabled(smashed);
    this.wheelDebris.setEnabled(smashed);
    for (let i = 0; i < this.breaches.length; i++) {
      const severity = breachSeverity(d.leaks, d.breaches, i);
      this.severity[i] = severity;
      const breach = this.breaches[i]!;
      breach.setEnabled(severity > 0);
      // Grows across the planking (y, z) only: x runs through the hull, where the hole's faces sit on the planks.
      const size = 0.5 + 0.3 * severity;
      breach.scaling.set(1, size, size);
    }
  }

  /**
   * Anchor and capstan from the replicated depth: the head turns with the rope wound in,
   * the rope pays out to the anchor ring, and the pawl rides the ratchet or is kicked clear.
   */
  updateAnchor(dt: number, depth: number, falling: boolean): void {
    // Teleports and joins snap; normal raises and drops ease out the patch steps.
    if (Math.abs(depth - this.anchorShown) > 0.4) this.anchorShown = depth;
    else this.anchorShown += (depth - this.anchorShown) * Math.min(1, dt * 12);
    this.applyAnchor(falling, dt);
  }

  private applyAnchor(falling: boolean, dt = 0): void {
    const depth = this.anchorShown, turn = capstanTurn(depth);
    this.capstan.rotation.y = turn;
    this.anchor.position.y = ANCHOR.y - depth * ANCHOR.drop;
    this.anchorRope.scaling.y = this.anchorRope.position.y - this.anchor.position.y - ANCHOR_RING_TOP;
    this.capstanCoil.scaling.y = 0.2 + 0.8 * (1 - depth);
    const teeth = (turn * SHIP.capstanTeeth) / (Math.PI * 2);
    const pawl = falling ? PAWL_KICK : PAWL_TOOTH_LIFT * (teeth - Math.floor(teeth));
    this.capstanPawl.rotation.x = dt <= 0 ? pawl : this.capstanPawl.rotation.x + (pawl - this.capstanPawl.rotation.x) * Math.min(1, dt * 30);
  }

  dispose(): void {
    for (let i = 0; i < this.sailDamage.length; i++) {
      this.sailDamage[i]?.dispose();
      this.sailDamage[i] = null;
    }
    this.root.dispose();
  }

  /** Owner look angles are ship-local, matching the server's firing geometry. A wrecked gun lies off its smashed carriage. */
  updateCannons(cannons: Iterable<CannonState>, players: { get(id: string): Pick<PlayerState, "yaw" | "pitch"> | undefined }): void {
    let i = 0;
    for (const cannon of cannons) {
      const pivot = this.cannonBarrels[i];
      const mount = SHIP_POINTS.cannons[i];
      if (!pivot || !mount) break;
      const bit = 1 << i, was = (this.wreckedBits & bit) !== 0;
      if (cannon.wrecked !== was) {
        this.wreckedBits ^= bit;
        this.carriages[i]!.setEnabled(!cannon.wrecked);
        this.wrecks[i]!.setEnabled(cannon.wrecked);
        pivot.position.copyFrom(cannon.wrecked ? this.fallen[i]!.position : this.barrelRest[i]!);
      }
      if (cannon.wrecked) {
        cannonFallenRotation(i, this.fallenRotation);
        pivot.rotation.set(this.fallenRotation.x, this.fallenRotation.y, this.fallenRotation.z);
      } else {
        const owner = players.get(cannon.owner);
        cannonAim(i, owner?.yaw ?? (mount.side * Math.PI) / 2, owner?.pitch ?? 0, this.aim);
        pivot.rotation.set(-this.aim.elevation, this.aim.yaw, 0);
      }
      i++;
    }
  }

  /** Shows a fresh patch where a leak was repaired. */
  markPatched(slot: number): void {
    this.patches[slot]?.setEnabled(true);
  }

  resetPatches(): void {
    for (const p of this.patches) p.setEnabled(false);
  }
}

/** Fixed shore carriage with a world-relative animated barrel, never statically merged. */
export class IslandCannonModel {
  readonly root: TransformNode;
  readonly barrelNode: TransformNode;
  private readonly meshes: Mesh[];
  private readonly aim = { yaw: 0, elevation: 0 };

  constructor(scene: Scene, private readonly env: Environment, private readonly mount: IslandCannonMount) {
    const model = spawnModel(scene, env.mats, "cannon");
    this.root = model.root;
    this.root.position.set(mount.x, mount.y, mount.z);
    this.barrelNode = model.part("cannon0");
    this.meshes = model.meshes;
    for (const mesh of this.meshes) {
      mesh.receiveShadows = true;
      env.addCaster(mesh);
    }
    this.update(undefined);
  }

  update(owner: Pick<PlayerState, "yaw" | "pitch"> | undefined): void {
    islandCannonAim(this.mount.id, owner?.yaw ?? this.mount.heading, owner?.pitch ?? 0, this.aim);
    this.barrelNode.rotation.set(-this.aim.elevation, this.aim.yaw, 0);
  }

  dispose(): void {
    for (const mesh of this.meshes) this.env.removeCaster(mesh);
    this.root.dispose();
  }
}
